import { useState, useEffect, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { createList, smartListPreview } from '../../utils/listsApi';

const FILTER_TYPES = [
  { value: 'status', label: 'Status', options: ['solved', 'attempted', 'todo'] },
  { value: 'difficulty', label: 'Difficulty', options: ['easy', 'medium', 'hard'] },
  { value: 'topics', label: 'Topics', options: ['array', 'string', 'linked-list', 'tree', 'graph', 'dynamic-programming', 'math', 'stack', 'hash-table', 'binary-search', 'two-pointers', 'sliding-window'] },
  { value: 'language', label: 'Language', options: ['JavaScript', 'Python3', 'Java', 'C++'] }
];

const OPERATORS = [{ value: 'is', label: 'is' }, { value: 'is not', label: 'is not' }];

export default function EditSmartListRuleModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [match, setMatch] = useState('all');
  const [filters, setFilters] = useState([{ type: 'status', operator: 'is not', value: 'attempted' }, { type: 'difficulty', operator: 'is', value: 'medium' }]);
  const [count, setCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchCount = useCallback(async () => {
    setLoadingCount(true);
    try {
      const c = await smartListPreview({ match, filters: filters.filter(f => f.type && f.value) });
      setCount(c);
    } catch (e) {
      setCount(0);
    } finally {
      setLoadingCount(false);
    }
  }, [match, filters]);

  useEffect(() => {
    const t = setTimeout(fetchCount, 400);
    return () => clearTimeout(t);
  }, [fetchCount]);

  const addRule = () => {
    const used = new Set(filters.map(f => f.type));
    const nextType = FILTER_TYPES.find(ft => !used.has(ft.value))?.value || 'difficulty';
    setFilters(prev => [...prev, { type: nextType, operator: 'is', value: FILTER_TYPES.find(ft => ft.value === nextType)?.options?.[0] || 'easy' }]);
  };

  const removeRule = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const updateFilter = (index, field, value) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Enter a list name');
      return;
    }
    const validFilters = filters.filter(f => f.type && f.value);
    if (validFilters.length === 0) {
      setError('Add at least one filter');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const list = await createList({
        name: trimmedName,
        description: 'Smart list',
        isPrivate: true,
        isSmart: true,
        rules: { match, filters: validFilters }
      });
      onCreated?.(list);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create list');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-background-card dark:bg-background-primary border border-border-primary rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <div>
            <h2 className="text-lg font-bold text-text-primary">Edit Rule</h2>
            <p className="text-sm text-text-secondary mt-0.5">Customize your problemset with multiple rules like difficulty and topics.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-background-secondary text-text-secondary">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {error && <p className="text-sm text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">List name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 30))}
              placeholder="e.g. Medium DP"
              className="w-full px-3 py-2 rounded-lg bg-background-secondary border border-border-primary text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
              maxLength={30}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-text-primary">Match</span>
            <select
              value={match}
              onChange={(e) => setMatch(e.target.value)}
              className="px-2 py-1.5 rounded-lg bg-background-secondary border border-border-primary text-text-primary text-sm"
            >
              <option value="all">All</option>
              <option value="any">Any</option>
            </select>
            <span className="text-sm text-text-secondary">of the following filters:</span>
          </div>
          <div className="space-y-2">
            {filters.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-background-secondary">
                <select
                  value={f.type}
                  onChange={(e) => updateFilter(i, 'type', e.target.value)}
                  className="px-2 py-1.5 rounded bg-background-primary border border-border-primary text-text-primary text-sm"
                >
                  {FILTER_TYPES.map(ft => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
                <select
                  value={f.operator}
                  onChange={(e) => updateFilter(i, 'operator', e.target.value)}
                  className="px-2 py-1.5 rounded bg-background-primary border border-border-primary text-text-primary text-sm"
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <select
                  value={f.value}
                  onChange={(e) => updateFilter(i, 'value', e.target.value)}
                  className="px-2 py-1.5 rounded bg-background-primary border border-border-primary text-text-primary text-sm min-w-[120px]"
                >
                  {FILTER_TYPES.find(ft => ft.value === f.type)?.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  )) || <option value={f.value}>{f.value}</option>}
                </select>
                <button type="button" onClick={() => removeRule(i)} className="p-1.5 rounded text-text-tertiary hover:text-red-500 hover:bg-red-500/10">−</button>
              </div>
            ))}
            <button type="button" onClick={addRule} className="flex items-center gap-2 text-sm text-button-primary-bg hover:underline">
              + Add Rule
            </button>
          </div>
          <p className="text-sm text-text-secondary flex items-center gap-2">
            {loadingCount ? 'Updating…' : `Including ${count.toLocaleString()} question${count !== 1 ? 's' : ''} and updating by conditions.`}
          </p>
        </div>

        <div className="p-4 border-t border-border-primary flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border-primary text-text-primary hover:bg-background-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
